import axiosInstance from './axiosInstance';
import logger from '../utils/logger';
import {
    CertCategory,
    CertReviewStatus,
    CertSourceChannel,
    WorkerCertification,
} from '../models/worker';

// Allowlist for the dashboard cert upload input (the "Upload file" button).
// Keep in sync with the backend allowlist at:
//   optima-backend/src/modules/worker-certifications/worker-certifications.enums.ts
//   (ALLOWED_CERTIFICATION_EXTENSIONS / ALLOWED_CERTIFICATION_MIME_TYPES)
// Note: NOT used for the "Upload supporting image" (attach-image) input —
// that path keeps a wider legacy allowlist via the backend stageFile route.
export const ALLOWED_CERTIFICATION_EXTENSIONS = [
    '.pdf',
    '.jpg',
    '.jpeg',
    '.png',
    '.heic',
    '.heif',
    '.webp',
    '.doc',
    '.docx',
] as const;

export interface CreateManualCertPayload {
    workerId: number;
    proposedCertName: string;
    issuer?: string;
    certCategory?: CertCategory;
    issueDate?: string;
    expiryDate?: string;
    level?: string;
    certNumber?: string;
}

export interface UpdateCertPayload {
    proposedCertName?: string;
    certCategory?: CertCategory;
    issuer?: string;
    holderName?: string;
    holderDob?: string;
    certNumber?: string;
    issueDate?: string;
    expiryDate?: string;
    level?: string;
}

export interface ReviewQueueQuery {
    status?: CertReviewStatus[];
    workerId?: number;
    certCategory?: CertCategory;
    sourceChannel?: CertSourceChannel;
    hasFlags?: boolean;
    page?: number;
    limit?: number;
}

export interface CertNameSuggestion {
    proposedCertName: string;
    count: number;
}

export interface ReviewQueueResponse {
    items: WorkerCertification[];
    total: number;
}

export const fetchCertNameSuggestions = async (): Promise<CertNameSuggestion[]> => {
    try {
        const { data } = await axiosInstance.get<CertNameSuggestion[]>(
            '/worker-certifications/cert-name-suggestions',
        );
        return data;
    } catch (err) {
        logger.error('Error fetching cert-name suggestions');
        throw err;
    }
};

export const fetchReviewQueue = async (query: ReviewQueueQuery): Promise<ReviewQueueResponse> => {
    try {
        const { data } = await axiosInstance.get<ReviewQueueResponse>(
            '/worker-certifications/review-queue',
            {
                params: query,
                // Axios default serialises arrays as `status[]=A&status[]=B`,
                // which Nest's query parser leaves as a literal `status[]` key.
                // Emit repeated keys instead: `status=A&status=B`.
                paramsSerializer: {
                    indexes: null,
                },
            },
        );
        return data;
    } catch (err) {
        logger.error('Error fetching cert review queue');
        throw err;
    }
};

export const fetchCertById = async (id: string): Promise<WorkerCertification> => {
    const { data } = await axiosInstance.get<WorkerCertification>(`/worker-certifications/${id}`);
    return data;
};

export const fetchWorkerCertifications = async (workerId: number): Promise<WorkerCertification[]> => {
    const { data } = await axiosInstance.get<WorkerCertification[]>(
        `/workers/${workerId}/certifications`,
    );
    return data;
};

export const createManualCert = async (
    payload: CreateManualCertPayload,
    attachedImages?: File[],
): Promise<WorkerCertification> => {
    // No images → plain JSON post (existing MANUAL_UNVERIFIED behaviour).
    if (!attachedImages || attachedImages.length === 0) {
        const { data } = await axiosInstance.post<WorkerCertification>(
            '/worker-certifications/manual',
            payload,
        );
        return data;
    }

    // Step 10: 1..N images → multipart. Backend stitches into a single PDF
    // via optima-ai and stores it as the cert's canonical source, status
    // becomes PENDING_REVIEW. PDFs are NOT accepted on this endpoint; use
    // /upload if the recruiter has a PDF.
    const form = new FormData();
    Object.entries(payload).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') {
            form.append(k, String(v));
        }
    });
    attachedImages.forEach(file => form.append('attachedImages', file));
    const { data } = await axiosInstance.post<WorkerCertification>(
        '/worker-certifications/manual',
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
};

/**
 * Image-only allowlist for the manual-create modal's attachment input.
 * Mirrors backend behavior at `worker-certifications.controller.ts`'s
 * createManual fileFilter — PDFs are explicitly rejected.
 */
export const ALLOWED_MANUAL_ATTACHMENT_EXTENSIONS = ALLOWED_CERTIFICATION_EXTENSIONS
    .filter(e => e !== '.pdf');

export const MAX_MANUAL_ATTACHMENTS = 10;

/**
 * v7 upload response shape — the controller no longer creates a row sync.
 * Instead it hands off to a Bull worker (see optima-backend/assets/flows/
 * cert-ingestion-job-lifecycle.md):
 *   - `jobId` populated → new ingest, frontend listens for Socket.IO
 *     `cert_upload_completed` (or polls upload-jobs/:jobId as fallback)
 *   - `jobId: null` + `dedupedCertIds` populated → dedup hit, rows already
 *     exist, frontend can refresh immediately
 */
export interface UploadCertResponse {
    jobId: string | null;
    dedupedCertIds: string[];
}

export const uploadCert = async (
    workerId: number,
    file: File,
): Promise<UploadCertResponse> => {
    const form = new FormData();
    form.append('workerId', String(workerId));
    form.append('file', file);
    const { data } = await axiosInstance.post<UploadCertResponse>(
        '/worker-certifications/upload',
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
};

/**
 * Polling fallback when Socket.IO is disconnected. Reads Bull job state
 * server-side.
 *
 * Poll every ~5s until status !== 'pending'. Stop polling once the Socket.IO
 * connection comes back if you've been listening for the event in parallel.
 */
export interface UploadJobStatus {
    status: 'pending' | 'completed' | 'failed';
    certIds?: string[];
    failedCertIds?: string[];
    reason?: string;
}

export const getUploadJobStatus = async (jobId: string): Promise<UploadJobStatus> => {
    const { data } = await axiosInstance.get<UploadJobStatus>(
        `/worker-certifications/upload-jobs/${encodeURIComponent(jobId)}`,
    );
    return data;
};

export const attachImageToCert = async (
    id: string,
    file: File,
): Promise<WorkerCertification> => {
    const form = new FormData();
    form.append('file', file);
    const { data } = await axiosInstance.post<WorkerCertification>(
        `/worker-certifications/${id}/attach-image`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
};

export const updateCert = async (
    id: string,
    payload: UpdateCertPayload,
): Promise<WorkerCertification> => {
    const { data } = await axiosInstance.patch<WorkerCertification>(
        `/worker-certifications/${id}`,
        payload,
    );
    return data;
};

export const confirmCert = async (id: string): Promise<WorkerCertification> => {
    const { data } = await axiosInstance.post<WorkerCertification>(
        `/worker-certifications/${id}/confirm`,
    );
    return data;
};

export const rejectCert = async (
    id: string,
    rejectionReason: string,
): Promise<WorkerCertification> => {
    const { data } = await axiosInstance.post<WorkerCertification>(
        `/worker-certifications/${id}/reject`,
        { rejectionReason },
    );
    return data;
};

export const requestCertReupload = async (id: string): Promise<WorkerCertification> => {
    const { data } = await axiosInstance.post<WorkerCertification>(
        `/worker-certifications/${id}/request-reupload`,
    );
    return data;
};

/**
 * Re-run AI extraction on the existing cert row's canonical file. Updates the
 * row in place — proposed fields will be overwritten when the extraction
 * response lands (typically 1–3s).
 */
export const reextractCert = async (id: string): Promise<WorkerCertification> => {
    const { data } = await axiosInstance.post<WorkerCertification>(
        `/worker-certifications/${id}/re-extract`,
    );
    return data;
};

export const deleteCert = async (id: string): Promise<void> => {
    await axiosInstance.delete(`/worker-certifications/${id}`);
};

/**
 * Promote an existing worker_file (fileType=CERTIFICATION) into the v7 cert
 * pipeline. Used after a dashboard upload of a CERTIFICATION file, and by the
 * recruiter "Reassess" button on worker file rows.
 *
 * Same return shape as `uploadCert`:
 *   - jobId !== null → ingestion queued; subscribe to Socket.IO
 *     `cert_upload_completed:${jobId}` (or poll upload-jobs/:jobId) to know
 *     when the K rows have landed.
 *   - jobId === null + dedupedCertIds non-empty → byte-identical file already
 *     ingested for this worker; rows already exist, refresh immediately.
 */
export const ingestCertFromWorkerFile = async (
    workerFileId: number,
): Promise<UploadCertResponse> => {
    const { data } = await axiosInstance.post<UploadCertResponse>(
        `/worker-certifications/from-worker-file/${workerFileId}`,
    );
    return data;
};
